// /src/utils/api.js
import axios from "axios";
import { toast } from "react-toastify";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (typeof window !== "undefined" &&
  window.location.hostname.includes("onrender.com")
    ? "https://territory-crm-prod-backend.onrender.com"
    : "http://127.0.0.1:8000");

/** Default request timeout (ms). Large map/aggregate payloads often exceed 15s in dev. */
const envTimeout = Number(process.env.REACT_APP_API_TIMEOUT);
export const DEFAULT_API_TIMEOUT =
  Number.isFinite(envTimeout) && envTimeout > 0 ? envTimeout : 90000;

/** Map data can return thousands of leads — allow extra headroom. */
export const MAP_DATA_TIMEOUT = Math.max(DEFAULT_API_TIMEOUT, 120000);

const API = axios.create({
  baseURL: `${API_BASE}/api/`,
  timeout: DEFAULT_API_TIMEOUT,
});

// ---- Token helpers
const getAccess = () => localStorage.getItem("access_token");
const getRefresh = () => localStorage.getItem("refresh_token");
const setAccess = (t) => localStorage.setItem("access_token", t);
const setRefresh = (t) => {
  if (t) localStorage.setItem("refresh_token", t);
  else localStorage.removeItem("refresh_token");
};
const clearAuth = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("user");
};

// ---- Attach access token on every request
API.interceptors.request.use(
  (config) => {
    const token = getAccess();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    
    // ✅ Block write operations for read-only users
    let user = null;
    try {
      const userStr = localStorage.getItem("user");
      if (userStr && userStr !== "undefined" && userStr !== "null") {
        user = JSON.parse(userStr);
      }
    } catch (err) {
      // Ignore parse errors
    }

    const isReadOnly = user?.is_read_only === true;
    const method = config.method?.toLowerCase();
    
    if (isReadOnly && ["post", "put", "patch", "delete"].includes(method)) {
      // Show error notification
      toast.error("Read-only mode: You cannot modify data", {
        position: "bottom-right",
        autoClose: 3000,
      });
      
      return Promise.reject({
        response: {
          status: 403,
          data: { detail: "Read-only mode: You cannot modify data" },
        },
        config,
      });
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// ---- Refresh coordination (prevents multiple simultaneous refresh calls)
let isRefreshing = false;
let refreshWaiters = [];

const notifyWaiters = (newAccess) => {
  refreshWaiters.forEach((resume) => resume(newAccess));
  refreshWaiters = [];
};

// ---- Response / refresh logic
API.interceptors.response.use(
  (response) => {
    // Check if response has pagination structure (DRF pagination)
    if (
      response.data &&
      typeof response.data === "object" &&
      "results" in response.data &&
      Array.isArray(response.data.results) &&
      ("count" in response.data || "next" in response.data || "previous" in response.data)
    ) {
      // Store pagination metadata in response for reference if needed
      response.pagination = {
        count: response.data.count,
        next: response.data.next,
        previous: response.data.previous,
      };
      // Unwrap to return just the results array
      response.data = response.data.results;
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // If no response (network/CORS), fail fast
    if (!error.response) {
      return Promise.reject(error);
    }

    // Only handle 401 once per request
    if (error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = getRefresh();
      if (!refreshToken) {
        clearAuth();
        // preserve intended path (optional)
        const path = window.location.pathname + window.location.search;
        window.location.href = `/login?next=${encodeURIComponent(path)}`;
        return Promise.reject(error);
      }

      // Queue if a refresh is already in progress
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshWaiters.push((newAccess) => {
            if (!newAccess) return reject(error);
            originalRequest.headers.Authorization = `Bearer ${newAccess}`;
            resolve(API(originalRequest));
          });
        });
      }

      // Perform the refresh
      try {
        isRefreshing = true;
        const res = await axios.post(
          `${API_BASE}/api/token/refresh/`,
          { refresh: refreshToken },
          {
            headers: { "Content-Type": "application/json" },
            timeout: Math.min(DEFAULT_API_TIMEOUT, 60000),
          }
        );

        const newAccess = res.data?.access;
        const newRefresh = res.data?.refresh; // present if ROTATE_REFRESH_TOKENS=True
        if (!newAccess) throw new Error("No access token in refresh response");

        setAccess(newAccess);
        setRefresh(newRefresh);

        notifyWaiters(newAccess);
        originalRequest.headers.Authorization = `Bearer ${newAccess}`;
        return API(originalRequest);
      } catch (refreshErr) {
        // hard stop: clear and redirect once
        clearAuth();
        notifyWaiters(null);
        const path = window.location.pathname + window.location.search;
        window.location.href = `/login?next=${encodeURIComponent(path)}`;
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    // For other statuses, just bubble up
    return Promise.reject(error);
  }
);

// ---- Email Preferences
export const updateEmailPreferences = (data) =>
  API.patch("users/email-preferences/", data);

export const getEmailPreferences = () =>
  API.get("users/email-preferences/");

// ---- User data helpers
/**
 * Store user data in localStorage, ensuring is_read_only is always included.
 * @param {Object} userData - User data object from API
 */
export const storeUserData = (userData) => {
  const userWithReadOnly = {
    ...userData,
    is_read_only: userData.is_read_only ?? false,  // ✅ Ensure is_read_only is always included
    can_view_additional_notes: userData.can_view_additional_notes !== false,
  };
  localStorage.setItem("user", JSON.stringify(userWithReadOnly));
  return userWithReadOnly;
};

export default API;
